import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { bindActionCreators } from 'redux';

import {
    Button,
} from '@material-ui/core';

import {
    HubConnectionBuilder, LogLevel, HttpTransportType,
} from '@microsoft/signalr';

import videoPeerConnections from './VideoPeerConnection';

import apiUrls from '../../youwell-common/apiUrls';
import { sendError } from '../../youwell-common/errorUtils';

import * as UserStore from '../../app/store/UserStore';


const styles = theme => ({
    root: {
    },
    startActions: {
        padding: theme.spacing(2),
        display: 'flex',
        justifyContent: 'center',
    },
    videos: {
        padding: theme.spacing(1),
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
    },
    video: {
        margin: theme.spacing(2),
    },
    inCallActions: {
        margin: theme.spacing(4),
        display: 'flex',
        justifyContent: 'center',
    },
});


class Videochat extends React.Component {
    state = {
        isInTheCall: false,
        isBeingCalled: false,
        isReady: false,
        infoText: '',
    }

    componentWillUnmount() {
        if (this.signalRConnection) {
            this.signalRConnection.stop().catch(this.onError);
        }
    }

    onError = (error) => {
        sendError({ sender: 'Videochat', error: error && error.toString() });
    }

    onCreateAnswer = (answer) => {
        if (this.signalRConnection) {
            this.signalRConnection.send('FinalizeJoinVideoCallRoom', answer)
                .catch(this.onError);
        }
    }

    onSendCandidate = (candidate) => {
        this.signalRConnection.send('SendIceCandidate', candidate)
            .catch(this.onError);
    }

    onReceiveCandidate = (id, type, candidate) => {
        if (id === this.props.profile.id) {
            videoPeerConnections.receiveIceCandidate(candidate);
        }
    }

    onRemoteStream = (remoteStream) => {
        if (this.remoteVideo && this.remoteVideo.srcObject !== remoteStream) {
            this.remoteVideo.srcObject = remoteStream;
        }
    }

    onTrackRemoved = () => {
        if (this.remoteVideo && this.remoteVideo.srcObject) {
            const stream = this.remoteVideo.srcObject;
            const trackList = stream.getTracks();
            if (trackList.length === 0) {
                this.endCall();
            }
        }
    }

    onPeerDisconnected = () => {
        this.endCall();
    }

    onSignalrCloseVideocall = () => {
        this.endCall();
    }

    onSignalrReceiveSDP = (sdp) => {
        if (this.hasVideoStreamStarted === false) {
            this.hasVideoStreamStarted = true;
            if (videoPeerConnections) {
                this.getUserMedia(
                    { min: 160, ideal: 320, max: 320 },
                    { min: 120, ideal: 240, max: 240 },
                ).then((video) => {
                    videoPeerConnections.startPeerConnection(this.props.profile.id,
                        this.onSendCandidate,
                        this.onRemoteStream,
                        this.onTrackRemoved,
                        this.onPeerDisconnected);
                    videoPeerConnections.setupPeerConnectionStream(video);
                    videoPeerConnections.answerPeerConnection(this.props.profile.id, sdp, this.onCreateAnswer);
                })
                    .catch(this.onError);
            }
        }
    }

    onSignalrJoinVideocallFailed = () => {
        this.setState({ isInTheCall: false, isBeingCalled: false });
    }

    onSignalRStart = () => {
        this.hasVideoStreamStarted = false;
        if (this.signalRConnection) {
            const data = {
                target: this.props.profile.id,
                type: 'join-videocall',
            };
            this.signalRConnection.send('JoinVideoCallRoom', data)
                .catch(this.onError);
        }
    }

    getUserMedia = (width, height) => new Promise((resolve, reject) => {
        navigator.getUserMedia = navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia;
        const op = {
            video: {
                width,
                height,
            },
            audio: true,
        };

        if (navigator.getUserMedia) {
            navigator.getUserMedia(op, (stream) => {
                if (this.localVideo) {
                    this.localVideo.srcObject = stream;
                }
                resolve(stream);
            }, (err) => {
                reject(err);
            });
        }
    })

    call = () => {
        this.setState({ isInTheCall: true }, this.onSignalRStart);
    }

    beReadyForCall = () => {
        this.setState({ isReady: true, infoText: this.props.translate('videoChat.notBeingCalled') }, () => {
            if (this.signalRConnection) {
                const data = {
                    target: this.props.profile.id,
                    type: 'ready-videocall',
                    data: '',
                };
                this.signalRConnection.send('PatientIsWaiting', data)
                    .catch(this.onError);
            }
        });
    }

    endCall = () => {
        if (this.localVideo && this.localVideo.srcObject) {
            this.localVideo.srcObject.getTracks().forEach(track => track.stop());
        }
        if (this.remoteVideo && this.remoteVideo.srcObject) {
            this.remoteVideo.srcObject.getTracks().forEach(track => track.stop());
        }
        this.setState({ isInTheCall: false,
            isBeingCalled: false,
            isReady: false,
            infoText: this.props.translate('videoChat.callInterrupted') }, () => {
            if (this.signalRConnection) {
                const data = {
                    target: this.props.profile.id,
                    type: 'leave-videocall',
                    data: '',
                };
                this.signalRConnection.send('LeaveVideoCallRoom', data)
                    .catch(this.onError);
            }
            videoPeerConnections.endPeerConnection();
        });
    }

    onSignalrAllowVideocall = (id) => {
        if (id === this.props.profile.id) {
            this.setState({ isBeingCalled: true });
        }
    }

    onCallInterrupted = () => {
        if (this.state.isInTheCall) {
            this.endCall();
        } else if (this.state.isBeingCalled) {
            this.setState({ isBeingCalled: false, isReady: true, infoText: this.props.translate('videoChat.notBeingCalled') }, () => {});
        }
    }

    onPingPatient = () => {
        if (this.state.isReady && this.signalRConnection) {
            const data = {
                target: this.props.profile.id,
                type: 'ready-videocall',
                data: '',
            };
            this.signalRConnection.send('PatientIsWaiting', data)
                .catch(this.onError);
        }
    }

    createConnection = () => {
        if (!this.signalRConnection) {
            const options = {
                transport: HttpTransportType.WebSockets,
            };
            this.signalRConnection = new HubConnectionBuilder()
                .configureLogging(LogLevel.Warning)
                .withUrl(`${apiUrls.baseUrl}/${apiUrls.socket.videoChat}`, options)
                .withAutomaticReconnect()
                .build();
            this.signalRConnection.on('CallerIsWaiting', this.onSignalrAllowVideocall);
            this.signalRConnection.on('CloseVideocall', this.onSignalrCloseVideocall);
            this.signalRConnection.on('ReceiveVideocallSDP', this.onSignalrReceiveSDP);
            this.signalRConnection.on('ReceiveIceCandidate', this.onReceiveCandidate); // this never fires?!
            this.signalRConnection.on('JoinVideoCallRoomFailed', this.onSignalrJoinVideocallFailed);
            this.signalRConnection.on('CallInterrupted', this.onCallInterrupted);
            this.signalRConnection.on('PingPatient', this.onPingPatient);

            this.signalRConnection.onclose(() => {

            });

            this.signalRConnection.start().then(() => {}).catch(this.onError);
        }
    }

    render() {
        const {
            classes, translate, profile,
        } = this.props;

        if (!profile) {
            return null;
        }

        if (this.state.infoText === '') {
            this.setState({ infoText: this.props.translate('videoChat.notReadyYet') });
        }

        this.createConnection();

        return (
            <div className={classes.root}>
                {this.state.isInTheCall ? (
                    <React.Fragment>
                        <div className={classes.videos}>
                            <video className={classes.video} autoPlay id="localVideo" muted ref={(video) => { this.localVideo = video; }} />
                            <video className={classes.video} autoPlay id="remoteVideo" ref={(video) => { this.remoteVideo = video; }}><track kind="captions" /></video>
                        </div>
                        <div className={classes.inCallActions}>
                            <Button onClick={this.endCall} color="primary" variant="outlined">{translate('videoChat.hangUp')}</Button>
                        </div>
                    </React.Fragment>
                ) : (
                    <div>
                        {this.state.isBeingCalled ? (
                            <div className={classes.startActions}>
                                <Button onClick={this.call} color="primary" variant="outlined">{translate('videoChat.answerTheCall')}</Button>
                            </div>
                        ) : (
                            <React.Fragment>

                                <div className={classes.startActions}>
                                    <p color="primary" variant="outlined">{this.state.infoText}</p>
                                </div>

                                {this.state.isReady ? (
                                    <></>
                                ) : (
                                    <>
                                        <div className={classes.startActions}>
                                            <Button onClick={this.beReadyForCall} color="primary" variant="outlined">{translate('videoChat.readyForACall')}</Button>
                                        </div>
                                    </>
                                )}
                            </React.Fragment>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

Videochat.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    profile: PropTypes.object,
};

Videochat.defaultProps = {
    profile: null,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
    profile: state.auth.profileData,
});

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(UserStore.actionCreators, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Videochat));
